/**
 * The account screen's controller: `profile.model.ts` wired to the person's
 * reads (`fetchCollections`, `fetchMyActivity`, `fetchAffiliationClaimsMade`,
 * `fetchViewingRooms`), the organisation's (`fetchOrgProfile`), and the writes
 * that change either record (`updateProfile`, `updateOrg`, `declareTypes`,
 * `claimAffiliation`, `withdrawAffiliationClaim`).
 *
 * It owns what the model cannot: the two open-or-closed form drafts, the busy
 * and saved flags, the chosen tab, and the reload nonce that re-reads the
 * affiliations after a claim. It does NOT own the account itself — `me` comes
 * from the route tree's context and every write goes back out through
 * `replaceMe`, which is why the sidebar's card updates in the same render.
 */

import type {
  DeclaredMemberType,
  ActivityEntry,
  AffiliationAudience,
  AffiliationClaim,
  CollectionSummary,
  MeResponse,
  OrgPrivilege,
  OrgProfileResponse,
  OrgRole,
  WorkspaceRef,
} from "@ondo/shared";
import { useCallback, useEffect, useState } from "react";

import { describeError } from "../collection/collection.model.ts";
import { toActivity } from "../dashboard/dashboard.model.ts";
import {
  claimAffiliation,
  fetchAffiliationClaimsMade,
  fetchCollections,
  endMembership,
  fetchEstateCatalogue,
  inviteColleague,
  fetchMyActivity,
  fetchOrgProfile,
  fetchViewingRooms,
  publishAffiliation,
  setMembershipRole,
  setRoleBundle,
  setSeatPrivilege,
  updateOrg,
  declareTypes,
  updateProfile,
  withdrawAffiliationClaim,
  withdrawInvitation,
} from "../lib/api.ts";
import { workspacePath } from "../routes/routes.model.ts";
import { ACTIVITY_ROW_LIMIT, PROFILE_COPY } from "./profile.constants.ts";
import type { ProfileTabKey } from "./profile.constants.ts";
import {
  activeTab,
  emptyInvite,
  levelFromChoice,
  subjectOf,
  tabsFor,
  toAffiliationRows,
  toContactFields,
  toContactValues,
  toEntityRows,
  toEstateSummary,
  toHoldingRows,
  toMembershipChoices,
  toOrgIdentity,
  toPersonIdentity,
  toInvitationRows,
  toSummary,
  grantFromChoice,
  roleFromChoice,
  toBundleRows,
  toTeamRows,
  toggleAudience,
  validateContact,
  validateInvite,
  validateOndoId,
  validateOrg,
} from "./profile.model.ts";
import type {
  ContactValues,
  InviteValues,
  OrgPanels,
  ProfileView,
} from "./profile.model.ts";

export interface ProfileController {
  view: ProfileView;
  chooseTab: (tab: ProfileTabKey) => void;

  /** The contact form: null when it is closed, values when it is open. */
  contactDraft: ContactValues | null;
  editContact: () => void;
  cancelContact: () => void;
  changeContact: (patch: Partial<ContactValues>) => void;
  saveContact: () => void;

  orgDraft: OrgPanels["values"] | null;
  editOrg: () => void;
  cancelOrg: () => void;
  changeOrg: (patch: Partial<OrgPanels["values"]>) => void;
  saveOrg: () => void;

  /**
   * Returns the refusal, or null when the claim was sent.
   *
   * `void` before, which meant the form had no way to tell an accepted claim
   * from a rejected one and cleared its three fields either way — so a typo in
   * an ONDO id lost the id, the title and the note, and said nothing. Same
   * shape as `onAttachOndoId` on the provenance side, for the same reason.
   */
  addAffiliation: (input: { ondoId: string; title: string; note: string }) => string | null;
  removeAffiliation: (claimId: string) => void;
  /**
   * Who sees one relationship — one audience at a time, as a checkbox does.
   *
   * The CURRENT set is read from this hook's own state rather than passed in by
   * the view, which is what keeps the view free of the composition: a checkbox
   * knows which box was ticked and nothing about the other two. `toggleAudience`
   * applies the nesting in both directions before the whole set goes out, since
   * the route takes every audience on every call.
   */
  setAffiliationAudience: (
    claimId: string,
    audience: AffiliationAudience,
    enabled: boolean,
  ) => void;
  /** Adds one declared type. There is no removal — see `addableMemberships`. */
  addMembership: (type: DeclaredMemberType) => void;

  /**
   * Ending a colleague's seat. OND-91.
   *
   * TWO PRESSES, which is why this is a membership id rather than a boolean: the
   * first press arms ONE row and the second fires it, and arming a second row
   * disarms the first. A single flag would let two rows be armed at once and the
   * reader would be one mis-click from ending the wrong person's access.
   */
  armedSeat: string | null;
  armSeat: (membershipId: string) => void;
  cancelSeat: () => void;
  endSeat: (membershipId: string) => void;

  /**
   * Setting a seat's named role, and where it departs from it. OND-61.
   *
   * ALL THREE TAKE THE CONTROL'S RAW STRING, and the mapping back to the three
   * server values lives in `profile.model.ts` — `roleFromChoice` and
   * `grantFromChoice`. "Empty means no named role" and "empty means follow the
   * role" are rules with real effects on what a colleague can do, and a view
   * deciding either would be a business condition in JSX.
   *
   * No second press on any of them. Ending a seat is armed because it cannot be
   * undone from inside the product; a role or an ability can be set straight
   * back, and a confirmation on a reversible act is a dialogue people learn to
   * dismiss.
   */
  setSeatRole: (membershipId: string, choice: string) => void;
  setSeatAbility: (membershipId: string, privilege: OrgPrivilege, choice: string) => void;
  setBundleLine: (role: OrgRole, privilege: OrgPrivilege, granted: boolean | null) => void;

  /**
   * Inviting a colleague. OND-24.
   *
   * A DRAFT RATHER THAN A FLAG, the shape `contactDraft` and `orgDraft` have and
   * for their reason: null means the form is closed, and an object means it is
   * open and holds exactly what has been typed. Three `useState`s and a boolean
   * would allow "closed but holding a half-typed address", which is the state
   * that makes a cancel ambiguous.
   *
   * NO SECOND PRESS, unlike ending a seat, and the asymmetry is the same one
   * `setSeatRole` is argued on: an invitation can be withdrawn from this very
   * panel, so it is reversible from inside the product. Ending a seat is not.
   */
  inviteDraft: InviteValues | null;
  startInvite: () => void;
  changeInvite: (patch: Partial<InviteValues>) => void;
  cancelInvite: () => void;
  sendInvite: () => void;
  withdrawInvite: (invitationId: string) => void;

  busy: boolean;
  /** A refusal from the last write, or a rule broken before one was attempted. */
  actionError: string | null;
  saved: boolean;
}

/**
 * The account screen: one subject, several reads, two writable records.
 *
 * `me` arrives from the account context rather than being fetched here — it is
 * loaded once for the whole route tree — and every write hands the server's
 * answer straight back to it through `replaceMe`. That is why saving a display
 * name updates the sidebar's identity card in the same render: there is one copy
 * of the account and this screen does not keep a second.
 *
 * The organisation is fetched, not derived from `me.memberships`. A membership
 * says the caller belongs; it carries no registered address, no roster and no
 * `canEdit`, and inferring the last of those from `level` would be a second
 * definition of admin living in the client.
 */
export function useProfileController(input: {
  me: MeResponse | null;
  workspace: WorkspaceRef;
  replaceMe: (me: MeResponse) => void;
}): ProfileController {
  const orgId = input.workspace.organisation?.ondoId ?? null;

  const [tab, setTab] = useState<ProfileTabKey | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [rooms, setRooms] = useState<number | null>(null);
  const [claims, setClaims] = useState<AffiliationClaim[] | null>(null);
  // Kept as they arrived and turned into rows at render, so "2 days ago" is read
  // from the clock now rather than frozen at fetch time. The dashboard's rule.
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  // Stored alongside the ONDO id it belongs to, and read back only when the two
  // agree — `useMe`'s arrangement, and here for both of its reasons. It avoids
  // clearing state synchronously inside the effect when the workspace loses its
  // organisation, and it means a roster fetched for one gallery can never be
  // displayed under another's name.
  const [org, setOrg] = useState<{ orgId: string; response: OrgProfileResponse } | null>(
    null,
  );
  /**
   * The estate's catalogue counts. OND-80.
   *
   * COUNTS, NOT ROWS. The profile states three figures and links to nothing, so
   * holding the works here would keep a catalogue of any size in memory for a
   * panel that renders two numbers out of it. `toCatalogueView` on the catalogue
   * screen does the same arithmetic over the same response, and that is the one
   * place the chips read — this is not a second definition, it is the same sum
   * taken once for a different screen.
   *
   * Stored with its org id, the way `org` above is and for the same two reasons:
   * a count fetched for one estate can never be shown under another's name.
   */
  const [catalogue, setCatalogue] = useState<{
    orgId: string;
    works: number;
    attested: number;
  } | null>(null);
  const loadedOrg = orgId !== null && org?.orgId === orgId ? org.response : null;
  const loadedCatalogue =
    orgId !== null && catalogue?.orgId === orgId ? catalogue : null;
  const [failure, setFailure] = useState<string | null>(null);

  const [contactDraft, setContactDraft] = useState<ContactValues | null>(null);
  const [orgDraft, setOrgDraft] = useState<OrgPanels["values"] | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [armedSeat, setArmedSeat] = useState<string | null>(null);
  // OND-24. Null means the invite form is closed; see `inviteDraft` on
  // `ProfileController` for why this is a draft rather than three fields and a
  // flag.
  const [inviteDraft, setInviteDraft] = useState<InviteValues | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  // Holdings and affiliations. Both belong to the PERSON and are fetched in
  // every workspace, because "Your account" is a tab on an organisation's
  // profile too and it shows the same panels.
  useEffect(() => {
    let active = true;

    fetchCollections()
      .then((rows) => {
        if (active) setCollections(rows);
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    // Empty is a real answer twice over — nothing recorded, or no feed store
    // configured for this deployment — so a failure here leaves the panel empty
    // rather than failing the screen.
    fetchMyActivity()
      .then((rows) => {
        if (active) setEntries(rows);
      })
      .catch(() => {
        if (active) setEntries([]);
      });

    fetchAffiliationClaimsMade()
      .then((rows) => {
        if (active) setClaims(rows);
      })
      .catch(() => {
        // Not fatal, and deliberately silent. An affiliations panel that failed
        // to load must not empty a profile whose other eight panels are fine —
        // the same judgement `signWorkThumbnails` gets on the collection grid.
        if (active) setClaims([]);
      });

    // A count, and only a count. A refusal here means the rooms row is left off
    // the summary rather than shown as zero: see `toSummary`.
    fetchViewingRooms()
      .then((rows) => {
        if (active) setRooms(rows.length);
      })
      .catch(() => {
        if (active) setRooms(null);
      });

    return () => {
      active = false;
    };
  }, [nonce]);

  useEffect(() => {
    if (!orgId) return;

    let active = true;

    fetchOrgProfile(orgId)
      .then((response) => {
        if (active) setOrg({ orgId, response });
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, [orgId, nonce]);

  /**
   * The catalogue, only on an estate's workspace.
   *
   * GATED ON THE ROLE, not attempted and allowed to fail. `GET /v1/estate/catalogue`
   * would answer a gallery's admin with an empty list rather than a refusal —
   * `ondo.estate_catalogue()` returns works attributed to a principal the caller
   * acts as, and a gallery is the authority for nobody — so a blind fetch would
   * print "0 works in the catalogue" on a firm that has no catalogue raisonné at
   * all. That is a false statement, not a small one.
   *
   * A REFUSAL LEAVES THE PANEL OFF, which is `toSummary`'s rule about viewing
   * rooms: "0" and "we do not know yet" are different sentences and only one of
   * them should be printed as a number. Silent, because a profile whose other
   * panels are fine must not fail over a count.
   */
  useEffect(() => {
    if (!orgId || input.workspace.role !== "estate") return;

    let active = true;

    fetchEstateCatalogue()
      .then((body) => {
        if (!active) return;
        setCatalogue({
          orgId,
          works: body.works.length,
          attested: body.works.filter((work) => work.attested).length,
        });
      })
      .catch(() => {
        if (active) setCatalogue(null);
      });

    return () => {
      active = false;
    };
  }, [orgId, input.workspace.role, nonce]);

  /**
   * Runs a write, then hands the answer on.
   *
   * `saved` is set on success and cleared the moment anything else happens, so
   * the confirmation cannot outlive the edit it belongs to.
   */
  const run = (action: Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setActionError(null);
    setSaved(false);

    action
      .then(() => {
        setSaved(true);
        after?.();
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /**
   * Ends a seat and takes the answer as the new truth.
   *
   * `endMembership` returns the whole profile, not an acknowledgement, and the
   * reason is `canEnd`: dropping to one administrator turns that flag false on
   * the remaining admin's own row, and a client patching its own copy of the
   * roster would go on offering a control the database now refuses.
   */
  const endSeat = (membershipId: string) => {
    if (!orgId) return;
    setArmedSeat(null);
    setBusy(true);
    setActionError(null);
    setSaved(false);

    endMembership(orgId, membershipId)
      .then((response) => {
        setOrg({ orgId, response });
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /**
   * Sets or clears a seat's named role — OND-61.
   *
   * TAKES THE RESPONSE AS THE NEW TRUTH, exactly as `endSeat` above does and
   * for a reason one step stronger: the answer carries that seat's RESOLVED
   * abilities after the floor and all four layers, and a client that patched
   * `role` on its own copy would have to re-run that resolution to know what
   * the row now says — which is the second definition of the rule the whole
   * ticket exists to keep in one place.
   */
  const setSeatRole = (membershipId: string, choice: string) => {
    if (!orgId) return;
    setBusy(true);
    setActionError(null);
    setSaved(false);

    setMembershipRole(orgId, membershipId, { role: roleFromChoice(choice) })
      .then((response) => {
        setOrg({ orgId, response });
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /**
   * Grants, refuses or clears one ability on one seat.
   *
   * THREE-VALUED, and the view passes the third value rather than deciding it:
   * `null` clears the exception so the bundle answers again, which is a
   * different act from refusing the ability outright. A two-state toggle here
   * would have made "this seat follows its role" unreachable once anybody had
   * pressed anything.
   */
  const setSeatAbility = (membershipId: string, privilege: OrgPrivilege, choice: string) => {
    if (!orgId) return;
    setBusy(true);
    setActionError(null);
    setSaved(false);

    setSeatPrivilege(orgId, membershipId, { privilege, granted: grantFromChoice(choice) })
      .then((response) => {
        setOrg({ orgId, response });
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /** Edits or clears one line of one of the firm's named-role bundles. */
  const setBundleLine = (
    role: OrgRole,
    privilege: OrgPrivilege,
    granted: boolean | null,
  ) => {
    if (!orgId) return;
    setBusy(true);
    setActionError(null);
    setSaved(false);

    setRoleBundle(orgId, { role, privilege, granted })
      .then((response) => {
        setOrg({ orgId, response });
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /**
   * Sends the invitation, and takes the response as the new truth — OND-24.
   *
   * `setOrg` rather than a reload, exactly as `endSeat` does it: the answer
   * carries the firm's whole invitation list, including the row this press just
   * created and whatever a worker has since recorded about delivery, and a
   * client appending its own optimistic row would be inventing the half the
   * server owns.
   *
   * VALIDATED FIRST AND THE FORM IS KEPT ON A REFUSAL. A rejected address is
   * nearly always a typo, so clearing the three fields would lose the job title
   * and the rung along with it — the bug `addAffiliation`'s own comment records
   * from the provenance side.
   */
  const sendInvite = () => {
    if (!orgId || !inviteDraft) return;

    const problem = validateInvite(inviteDraft);
    if (problem) {
      setActionError(problem);
      setSaved(false);
      return;
    }

    setBusy(true);
    setActionError(null);
    setSaved(false);

    inviteColleague(orgId, {
      email: inviteDraft.email.trim(),
      level: levelFromChoice(inviteDraft.level),
      // Trimmed to empty means no title. There is no previous value for the
      // distinction between "omitted" and "cleared" to be about — this is an
      // insert, not a replace.
      title: inviteDraft.title.trim() === "" ? null : inviteDraft.title.trim(),
    })
      .then((response) => {
        setOrg({ orgId, response });
        setInviteDraft(null);
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  /** Takes an unanswered invitation back. `setOrg` for `sendInvite`'s reason. */
  const withdrawInvite = (invitationId: string) => {
    if (!orgId) return;

    setBusy(true);
    setActionError(null);
    setSaved(false);

    withdrawInvitation(orgId, invitationId)
      .then((response) => {
        setOrg({ orgId, response });
        setSaved(true);
      })
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  return {
    armedSeat,
    armSeat: setArmedSeat,
    cancelSeat: () => setArmedSeat(null),
    endSeat,

    inviteDraft,
    startInvite: () => {
      setInviteDraft(emptyInvite());
      setActionError(null);
      setSaved(false);
    },
    changeInvite: (patch) =>
      setInviteDraft((values) => (values ? { ...values, ...patch } : values)),
    cancelInvite: () => {
      setInviteDraft(null);
      setActionError(null);
    },
    sendInvite,
    withdrawInvite,
    setSeatRole,
    setSeatAbility,
    setBundleLine,
    view: resolve({
      me: input.me,
      workspace: input.workspace,
      collections,
      rooms,
      claims,
      entries,
      org: loadedOrg,
      catalogue: loadedCatalogue,
      failure,
      tab,
      workBase: workspacePath(input.workspace, "work"),
    }),

    chooseTab: (next) => {
      setTab(next);
      setActionError(null);
      setSaved(false);
    },

    contactDraft,
    editContact: () => {
      if (input.me) setContactDraft(toContactValues(input.me));
      setActionError(null);
      setSaved(false);
    },
    cancelContact: () => {
      setContactDraft(null);
      setActionError(null);
    },
    changeContact: (patch) =>
      setContactDraft((values) => (values ? { ...values, ...patch } : values)),
    saveContact: () => {
      if (!contactDraft) return;

      const problem = validateContact(contactDraft);
      if (problem) {
        setActionError(problem);
        return;
      }

      run(
        updateProfile({
          displayName: contactDraft.displayName.trim(),
          // Trimmed to empty means cleared, which is what replace semantics are
          // for. `null` is the wire form of "hold nothing here".
          phone: contactDraft.phone.trim() === "" ? null : contactDraft.phone.trim(),
          country: contactDraft.country.trim() === "" ? null : contactDraft.country.trim(),
        }).then((me) => {
          input.replaceMe(me);
          setContactDraft(null);
        }),
      );
    },

    /**
     * Says the account is one more thing than it was.
     *
     * ADD ONLY — there is no counterpart that removes, and that is the rule
     * rather than an unfinished pair. See `addableMemberships`.
     *
     * `replaceMe` rather than a refetch, for `updateContact`'s reason one step
     * further on: the response IS the read, and this one moves `capacities`,
     * which moves `workspaces`, which moves the rail and the workspace
     * switcher. Re-requesting it would leave a window in which the sidebar
     * disagreed with the panel that had just been clicked.
     */
    addMembership: (type) => {
      run(declareTypes({ declaredTypes: [type] }).then(input.replaceMe));
    },

    orgDraft,
    editOrg: () => {
      if (loadedOrg) {
        setOrgDraft({
          displayName: loadedOrg.organisation.displayName,
          registeredAddress: loadedOrg.organisation.registeredAddress ?? "",
          registrationNumber: loadedOrg.organisation.registrationNumber ?? "",
        });
      }
      setActionError(null);
      setSaved(false);
    },
    cancelOrg: () => {
      setOrgDraft(null);
      setActionError(null);
    },
    changeOrg: (patch) =>
      setOrgDraft((values) => (values ? { ...values, ...patch } : values)),
    saveOrg: () => {
      if (!orgDraft || !orgId) return;

      const problem = validateOrg(orgDraft);
      if (problem) {
        setActionError(problem);
        return;
      }

      run(
        updateOrg(orgId, {
          displayName: orgDraft.displayName.trim(),
          registeredAddress:
            orgDraft.registeredAddress.trim() === ""
              ? null
              : orgDraft.registeredAddress.trim(),
          registrationNumber:
            orgDraft.registrationNumber.trim() === ""
              ? null
              : orgDraft.registrationNumber.trim(),
        }).then((response) => {
          setOrg({ orgId, response });
          setOrgDraft(null);
        }),
      );
    },

    addAffiliation: (values) => {
      const problem = validateOndoId(values.ondoId);
      if (problem) {
        setActionError(problem);
        return problem;
      }

      run(
        claimAffiliation({
          ondoId: values.ondoId.trim().toUpperCase(),
          title: values.title.trim() === "" ? null : values.title.trim(),
          note: values.note.trim() === "" ? null : values.note.trim(),
        }),
        reload,
      );

      return null;
    },
    removeAffiliation: (claimId) => run(withdrawAffiliationClaim(claimId), reload),
    setAffiliationAudience: (claimId, audience, enabled) => {
      const current = (claims ?? []).find((claim) => claim.claimId === claimId);
      const audiences = toggleAudience(current?.audiences ?? null, audience, enabled);
      // Null means the loaded claim carried no audience — the inbound read's
      // shape, which this screen never holds. Sending the default instead would
      // publish a relationship to everybody on a stray click.
      if (!audiences) return;

      run(publishAffiliation(claimId, { audiences }), reload);
    },

    busy,
    actionError,
    saved,
  };
}

/**
 * The view-model, assembled from whatever has arrived.
 *
 * `loading` is derived from the reads the CURRENT subject needs, not from all of
 * them: an organisation's profile does not wait on the affiliations call, and a
 * personal one never makes the org call at all. Deriving it from every state
 * would leave one screen spinning on a fetch the other one owns.
 */
function resolve(input: {
  me: MeResponse | null;
  workspace: WorkspaceRef;
  collections: CollectionSummary[] | null;
  rooms: number | null;
  claims: AffiliationClaim[] | null;
  entries: ActivityEntry[];
  org: OrgProfileResponse | null;
  /** The estate's catalogue counts, or null for a firm that has no catalogue. */
  catalogue: { works: number; attested: number } | null;
  failure: string | null;
  tab: ProfileTabKey | null;
  /** '/collector/me/work' — where a row about a work goes. */
  workBase: string;
}): ProfileView {
  if (input.failure) return { kind: "error", message: input.failure };
  if (!input.me || !input.collections) {
    return { kind: "loading", message: PROFILE_COPY.loading };
  }

  const subject = subjectOf(input.workspace);
  if (subject === "organisation" && !input.org) {
    return { kind: "loading", message: PROFILE_COPY.loading };
  }

  const me = input.me;
  const entities = toEntityRows(me);
  const holdings = toHoldingRows(input.collections);
  // OND-61. The bundles travel with the roster because `isCustomised` is a
  // comparison of two server answers — the seat's resolved abilities against
  // the firm's grid — and neither half means anything without the other.
  const team = input.org
    ? toTeamRows(input.org.members, input.me.principal.ondoId, input.org.bundles)
    : [];
  const bundles = input.org ? toBundleRows(input.org.bundles) : [];
  const tabs = tabsFor({
    subject,
    entities: entities.length,
    holdings: holdings.length,
    team: team.length,
  });
  return {
    kind: "ready",
    subject,
    tabs,
    active: activeTab(tabs, input.tab),
    person: {
      identity: toPersonIdentity(me),
      activity: toActivity({
        entries: input.entries,
        collections: input.collections,
        workBase: input.workBase,
        // The account this screen IS. Every row here is the reader's own trail,
        // but the actor is still not always them — a verification lands in both
        // parties' feeds, so "by you" and "by Sarah Kim" both belong on it.
        viewerOndoId: me.principal.ondoId,
        now: new Date(),
        limit: ACTIVITY_ROW_LIMIT,
      }),
      membership: toMembershipChoices(me.person.declaredTypes),
      contact: toContactFields(me),
      values: toContactValues(me),
      summary: toSummary({ collections: input.collections, rooms: input.rooms }),
      affiliations: toAffiliationRows(input.claims ?? []),
    },
    organisation: input.org
      ? {
          identity: toOrgIdentity(input.org.organisation),
          values: {
            displayName: input.org.organisation.displayName,
            registeredAddress: input.org.organisation.registeredAddress ?? "",
            registrationNumber: input.org.organisation.registrationNumber ?? "",
          },
          canEdit: input.org.organisation.canEdit,
          team,
          // OND-61. What each named role means at this firm, and which lines
          // the firm has edited away from the delegated defaults.
          bundles,
          // Whether this reader may change any of it. Taken off the ROSTER
          // rather than computed, because `can_set_role` is the same test the
          // write doors apply — the arrangement `canEnd` already has, so a
          // panel cannot offer a control the database would refuse. False when
          // the roster is empty, which cannot happen for a reader who can read
          // it at all.
          canSetRoles: team.some((row) => row.canSetRole),
          // OND-24. Empty for a reader who may not administer the firm's staff,
          // which is what the server answers them — and the panel is not drawn
          // at all in that case, so the two absences agree.
          invitations: toInvitationRows(input.org.invitations),
          // OND-80. Null unless this workspace is an estate AND the catalogue
          // read landed — see the effect that fetches it for why a refusal
          // leaves the panel off rather than printing zeroes.
          estate: input.catalogue
            ? toEstateSummary({ ...input.catalogue, team: team.length })
            : null,
        }
      : null,
    entities,
    holdings,
  };
}
